import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Flame, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckinCycle } from "@/components/checkin-cycle";
import { SpiritMascotsCard } from "@/components/spirit-mascots";
import {
  doCheckin,
  getLeaderboard,
  setStreakLeaderboard,
  type LeaderboardPeriod,
} from "@/lib/boide-api";
import { loadCheckinStatus, loadMyStreak } from "@/lib/bootstrap";
import { useBoideUser } from "@/hooks/use-boide-user";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/diem-danh")({
  head: () => ({
    meta: [
      { title: "Điểm Danh Hằng Ngày & Bảng Xếp Hạng Chuỗi Bói" },
      {
        name: "description",
        content:
          "Điểm danh mỗi ngày nhận thưởng và leo bảng xếp hạng chuỗi bói cùng linh vật phong thủy. Điểm danh ngay!",
      },
    ],
  }),
  component: DiemDanhPage,
});

const vnd = (n: number | null | undefined) =>
  typeof n === "number" ? n.toLocaleString("vi-VN") : "--";

const PERIOD_TABS: { key: LeaderboardPeriod; label: string }[] = [
  { key: "all", label: "Tất cả" },
  { key: "week", label: "Tuần này" },
  { key: "month", label: "Tháng này" },
];

function DiemDanhPage() {
  return (
    <main className="app-feature-page flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl px-3 py-6 pb-24 md:py-10">
        <header className="mb-6 text-center">
          <h1
            className="text-2xl font-bold text-gold-gradient md:text-3xl"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Điểm danh & Chuỗi bói
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Điểm danh mỗi ngày nhận thưởng, duy trì chuỗi để khai mở linh vật và leo bảng xếp hạng.
          </p>
        </header>

        <CheckinCard />
        <StreakCard />
        <LeaderboardCard />
      </div>
    </main>
  );
}

// ─── Điểm danh ────────────────────────────────────────────────────────────────
function CheckinCard() {
  const qc = useQueryClient();
  const { refresh } = useBoideUser();
  const status = useQuery({ queryKey: ["checkin-status"], queryFn: loadCheckinStatus });

  const checkin = useMutation({
    mutationFn: doCheckin,
    onSuccess: (res) => {
      if (res.success) {
        toast.success(res.message, {
          description: `+${vnd(res.reward)}đ · chuỗi ${res.streak} ngày`,
        });
        refresh();
      } else {
        toast.info(res.message);
      }
      qc.invalidateQueries({ queryKey: ["checkin-status"] });
      qc.invalidateQueries({ queryKey: ["streak-me"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Điểm danh thất bại"),
  });

  const data = status.data;

  return (
    <Card className="app-feature-card mb-5 border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <p className="text-xs font-extrabold tracking-[0.18em] text-primary">ĐIỂM DANH TÍCH LŨY</p>
        <CardTitle className="font-serif-soft text-2xl">Chu Kỳ 7 Ngày</CardTitle>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Điểm danh liên tục mỗi ngày để nhận xu miễn phí gieo quẻ và thỉnh ý AI. Đứt quãng chu kỳ
          sẽ tính lại từ đầu.
        </p>
      </CardHeader>
      <CardContent>
        {status.isLoading || !data ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : (
          <>
            <div className="mb-4">
              <CheckinCycle cycle={data.cycle} />
            </div>

            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Flame className="h-4 w-4 text-orange-400" />
                Chuỗi điểm danh:{" "}
                <span className="font-semibold text-foreground">{data.current_streak} ngày</span>
              </div>
              <Button
                onClick={() => checkin.mutate()}
                disabled={data.checked_in_today || checkin.isPending}
                className="w-full bg-gold-gradient text-primary-foreground shadow-gold hover:brightness-110 sm:w-auto"
              >
                {checkin.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {data.checked_in_today
                  ? "Đã điểm danh hôm nay"
                  : `Điểm danh nhận ${vnd(data.next_reward)}đ`}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Chuỗi bói của tôi ────────────────────────────────────────────────────────
function StreakCard() {
  const qc = useQueryClient();
  const streak = useQuery({ queryKey: ["streak-me"], queryFn: loadMyStreak });

  const toggle = useMutation({
    mutationFn: (join: boolean) => setStreakLeaderboard(join),
    onSuccess: (res) => {
      toast.success(res.message);
      qc.invalidateQueries({ queryKey: ["streak-me"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Cập nhật thất bại"),
  });

  const data = streak.data;

  return (
    <>
      <Card className="mb-5 border-gold-soft/40 bg-card/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Flame className="h-5 w-5 text-orange-400" />
            Chuỗi bói của bạn
          </CardTitle>
        </CardHeader>
        <CardContent>
          {streak.isLoading || !data ? (
            <Skeleton className="h-20 w-full rounded-xl" />
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Stat label="Hiện tại" value={`${data.current_streak}`} unit="ngày" />
                <Stat label="Dài nhất" value={`${data.longest_streak}`} unit="ngày" />
                <Stat label="Tổng ngày bói" value={`${data.total_boi_days}`} unit="ngày" />
              </div>
              <div className="mt-4 flex items-center justify-between rounded-xl border border-border/40 bg-background/40 px-4 py-3">
                <div className="text-sm">
                  <div className="font-medium text-foreground">Hiển thị trên bảng xếp hạng</div>
                  <div className="text-xs text-muted-foreground">
                    Tắt nếu bạn muốn ẩn tên khỏi BXH công khai.
                  </div>
                </div>
                <Switch
                  checked={data.join_leaderboard}
                  disabled={toggle.isPending}
                  onCheckedChange={(v) => toggle.mutate(v)}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
      {data ? (
        <SpiritMascotsCard
          currentStreak={data.current_streak}
          longestStreak={data.longest_streak}
        />
      ) : null}
    </>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="rounded-xl border border-border/40 bg-background/40 py-3">
      <div className="text-2xl font-bold text-gold-gradient">{value}</div>
      <div className="text-[11px] text-muted-foreground">
        {label} · {unit}
      </div>
    </div>
  );
}

// ─── Bảng xếp hạng ────────────────────────────────────────────────────────────
const LEADERBOARD_PER_PAGE = 10;

function LeaderboardCard() {
  const [period, setPeriodState] = useState<LeaderboardPeriod>("all");
  const [page, setPage] = useState(0);
  // Đổi kỳ → quay lại trang đầu.
  const setPeriod = (next: LeaderboardPeriod) => {
    setPeriodState(next);
    setPage(0);
  };
  const board = useQuery({
    queryKey: ["leaderboard", period, page],
    queryFn: () => getLeaderboard(period, LEADERBOARD_PER_PAGE, page * LEADERBOARD_PER_PAGE),
    placeholderData: (prev) => prev,
  });
  const data = board.data;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LEADERBOARD_PER_PAGE));

  return (
    <Card id="bang-xep-hang" className="scroll-mt-24 border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5 text-primary" />
          Bảng xếp hạng chuỗi bói
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as LeaderboardPeriod)}>
          <TabsList className="mb-4 grid w-full grid-cols-3">
            {PERIOD_TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} className="text-xs sm:text-sm px-1 sm:px-3">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {data && (data.my_rank || data.my_score > 0) ? (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-primary/40 bg-gold-gradient/10 px-4 py-2.5 text-sm">
            <span className="text-muted-foreground">Hạng của bạn</span>
            <span className="font-semibold text-foreground">
              {data.my_rank ? `#${data.my_rank}` : "Chưa xếp hạng"} ·{" "}
              {period === "all" ? `${data.my_score} ngày chuỗi` : `${data.my_score} ngày bói`}
            </span>
          </div>
        ) : null}

        {board.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        ) : !data || data.entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Chưa có ai trên bảng xếp hạng. Hãy là người đầu tiên!
          </p>
        ) : (
          <ul className="space-y-1.5">
            {data.entries.map((e, i) => {
              const isTop = e.rank <= 3;
              return (
                <li
                  key={e.user_id}
                  style={{ animationDelay: `${Math.min(i, 12) * 45}ms`, animationFillMode: "both" }}
                  className={`group flex animate-in items-center gap-3 rounded-xl border px-3 py-2 fade-in slide-in-from-bottom-2 transition-all duration-300 hover:-translate-y-0.5 ${
                    e.rank === data.my_rank
                      ? "border-primary/60 bg-[#E5B869]/15 shadow-[0_6px_24px_rgba(229,184,105,0.2)] hover:shadow-[0_8px_28px_rgba(229,184,105,0.3)]"
                      : "border-border/40 bg-background/40 hover:border-primary/40 hover:shadow-[0_6px_24px_rgba(229,184,105,0.12)]"
                  }`}
                >
                  <span className="w-7 shrink-0 text-center">
                    {isTop ? (
                      <span
                        className={`mx-auto grid h-6 w-6 place-items-center rounded-full border-2 border-white/70 text-[11px] font-bold text-white shadow-md ${e.rank === 1 ? "bg-[#C99100]" : e.rank === 2 ? "bg-[#929BA7]" : "bg-[#A95B24]"}`}
                      >
                        {e.rank}
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-muted-foreground">{e.rank}</span>
                    )}
                  </span>
                  <Avatar className="h-8 w-8 shrink-0 transition-transform duration-300 group-hover:scale-105">
                    {e.avatar ? <AvatarImage src={e.avatar} alt={e.display_name} /> : null}
                    <AvatarFallback className="bg-gold-gradient text-xs text-primary-foreground">
                      {e.display_name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {e.display_name}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-primary">
                    <Flame fill="currentColor" className="h-3.5 w-3.5" />
                    {e.score}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {data && totalPages > 1 ? (
          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 0 || board.isFetching}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Trước
            </Button>
            <span className="text-xs text-muted-foreground">
              Trang {page + 1}/{totalPages} · {total} người
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1 || board.isFetching}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="gap-1"
            >
              Sau
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
